import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { EquipmentMockApi } from '@vi-iot/client/apis/equipment/equipment.mock.api';
import { EventsHistoryApiPicker } from '@vi-iot/client/apis/events-history/events-history.api-picker';
import { PurchasesMockApi } from '@vi-iot/client/apis/purchases/purchases.mock.api';
import { RemoteDiagnosticsCloudMockApi } from '@vi-iot/client/apis/remote-diagnostics/remote-diagnostics-cloud.mock.api';

import { ActivityFeedApi } from './apis/activity-feed/activity-feed.api';
import { AnalyticsApi } from './apis/analytics/analytics.api';
import { AnnualReportApi } from './apis/annual-report/annual-report-api';
import { AuthenticationApi } from './apis/authentication/authentication.api';
import { BffInstallationsApi } from './apis/bff-installations/bff-installations.api';
import { BundleUpdateApi } from './apis/bundle-update/bundle-update.api';
import { CompanyApi } from './apis/company/company.api';
import { EquipmentApi } from './apis/equipment/equipment.api';
import { EventsHistoryApi } from './apis/events-history/events-history.api';
import { GatewayApi } from './apis/gateway/gateway.api';
import { GatewayUpdateApi } from './apis/gateway-update/gateway-update.api';
import { GeneralManagementApi } from './apis/general-management/general-management.api';
import { GeneralManagementApiPicker } from './apis/general-management/general-management.api-picker';
import { GeneralManagementMockApi } from './apis/general-management/general-management.mock-api';
import { HydraulicSchematicApi } from './apis/hydraulic-schematic/hydraulic-schematic.api';
import { InstllationsMigrationApi } from './apis/installations-migration/installations-migration.api';
import { InvitationsApiPicker } from './apis/invitations/invitations.api-picker';
import { InvitationsApi } from './apis/invitations/invitations.api';
import { LegalApi } from './apis/legal/legal.api';
import { LiveUpdatesApi } from './apis/live-updates/live-updates.api';
import { MonetizationApi } from './apis/monetization/monetization.api';
import { NotificationsApi } from './apis/notifications/notifications.api';
import { SrcNotificationSettingsApi } from './apis/src-notification-settings/src-notification-settings.api';
import { OperationalDataApi } from './apis/operational-data/operational-data.api';
import { PurchasesApi } from './apis/purchases/purchases.api';
import { RemoteConfigApi } from './apis/remote-config/remote-config.api';
import { RemoteDiagnosticsCloudApi } from './apis/remote-diagnostics/remote-diagnostics-cloud.api';
import { RemoteDiagnosticsCloudApiPicker } from './apis/remote-diagnostics/remote-diagnostics-cloud.api-picker';
import { RemoteDiagnosticsHelpersService } from './apis/remote-diagnostics/remote-diagnostics-helpers.service';
import { RemoteServiceApi } from './apis/remote-service/remote-service.api';
import { ServiceDocumentsApi } from './apis/service-documents/service-documents.api';
import { StatusPageApi } from './apis/status/status.api';
import { TranslationsApi } from './apis/translations/translations.api';
import { UserRegistrationApi } from './apis/user-registration/user-registration.api';
import { UsersApi } from './apis/users/users.api';
import { VitoguideBackendSettingsApi } from './apis/vitoguide-backend-settings/vitoguide-backend-settings.api';
import { VitoguideBackendSettingsApiPicker } from './apis/vitoguide-backend-settings/vitoguide-backend-settings.api-picker';
import { VitoguideBackendSettingsMockApi } from './apis/vitoguide-backend-settings/vitoguide-backend-settings.mock-api';
import { SmartHomeComponentsApi } from '@vi-iot/client/apis/smart-home-components/smart-home-components.api';
import { SmartHomeComponentsApiPicker } from '@vi-iot/client/apis/smart-home-components/smart-home-components.api-picker';
import { SmartHomeComponentsMockApi } from '@vi-iot/client/apis/smart-home-components/smart-home-components.mock-api';
import { ClientConfiguration, ClientService, CLIENT_CONFIG } from './client.service';
import { EquipmentApiPicker } from './apis/equipment/equipment.api-picker';
import { PurchasesApiPicker } from './apis/purchases/purchases.api-picker';
import { PowerLimitationHistoryApi } from './apis/power-limitation-history/power-limitation-history.api';

export function clientFactory(config: ClientConfiguration, http: HttpClient): any {
  return new ClientService(config, http);
}

export const defaultClientConfig: ClientConfiguration = {
  withCredentials: false,
  baseUrl: '',
  diagStackPoolManager: '',
  version: 'v2',
};

@NgModule({
  imports: [CommonModule, HttpClientModule],
  exports: [HttpClientModule],
})
export class ClientModule {
  static forRoot(config: ClientConfiguration): ModuleWithProviders<ClientModule> {
    return {
      ngModule: ClientModule,
      providers: [
        {
          provide: CLIENT_CONFIG,
          useValue: config || defaultClientConfig,
        },
        {
          provide: ClientService,
          useFactory: clientFactory,
          deps: [CLIENT_CONFIG, HttpClient],
        },
        OperationalDataApi,
        InvitationsApiPicker,
        InvitationsApi,
        MonetizationApi,
        UserRegistrationApi,
        LiveUpdatesApi,
        RemoteConfigApi,
        RemoteServiceApi,
        RemoteDiagnosticsCloudApi,
        RemoteDiagnosticsCloudApiPicker,
        RemoteDiagnosticsCloudMockApi,
        RemoteDiagnosticsHelpersService,
        UsersApi,
        CompanyApi,
        GeneralManagementApi,
        GeneralManagementApiPicker,
        GeneralManagementMockApi,
        HydraulicSchematicApi,
        InstllationsMigrationApi,
        EquipmentApi,
        EquipmentMockApi,
        EquipmentApiPicker,
        ServiceDocumentsApi,
        GatewayApi,
        GatewayUpdateApi,
        VitoguideBackendSettingsApi,
        VitoguideBackendSettingsApiPicker,
        VitoguideBackendSettingsMockApi,
        EventsHistoryApi,
        EventsHistoryApiPicker,
        ActivityFeedApi,
        StatusPageApi,
        TranslationsApi,
        NotificationsApi,
        SrcNotificationSettingsApi,
        AuthenticationApi,
        BffInstallationsApi,
        BundleUpdateApi,
        LegalApi,
        AnalyticsApi,
        AnnualReportApi,
        PurchasesApi,
        PurchasesMockApi,
        PurchasesApiPicker,
        SmartHomeComponentsApi,
        SmartHomeComponentsApiPicker,
        SmartHomeComponentsMockApi,
        PowerLimitationHistoryApi,
      ],
    };
  }
}
